"use client";

import { useEffect } from "react";
import { trackedFetch } from "@/lib/api/trackedFetch";

// Per-device marker set when the user explicitly turns notifications off. Push
// subscriptions can be dropped without permission changing, so the reconciler
// can't tell "lapsed on its own" from "user opted out" — this flag disambiguates
// so self-healing never re-subscribes someone who chose to turn it off.
export const PUSH_OPT_OUT_KEY = "push-opt-out";

// Fired on `window` after the reconciler silently re-subscribes and re-registers
// a lapsed subscription, so any open Settings UI can re-read its state instead of
// showing the stale pre-heal toggle for the rest of the page's lifetime.
export const PUSH_RECONCILED_EVENT = "push-subscription-reconciled";

export function PushSubscriptionReconciler() {
  useEffect(() => {
    const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
    if (isNative) return;
    if (
      !("serviceWorker" in navigator) ||
      !("PushManager" in window) ||
      !("Notification" in window)
    ) {
      return;
    }
    if (Notification.permission !== "granted") return;
    if (localStorage.getItem(PUSH_OPT_OUT_KEY) === "1") return;

    let cancelled = false;

    async function reconcile() {
      const publicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
      if (!publicKey) return;

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager
          .subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
          })
          .catch(() => null);
      }
      if (!sub || cancelled) return;

      const { supabase } = await import("@/lib/supabaseClient");
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token || cancelled) return;

      const res = await trackedFetch("/api/notifications/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscription: sub.toJSON(),
          userAgent: navigator.userAgent,
        }),
      }).catch(() => null);
      if (!res?.ok || cancelled) return;

      window.dispatchEvent(new Event(PUSH_RECONCILED_EVENT));
    }

    reconcile().catch(() => {});

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "push-subscription-changed") {
        reconcile().catch(() => {});
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener("message", onMessage);
    };
  }, []);

  return null;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}
